import { assertUnreachable } from "@arti/utilities";
import { AnimatePresence, motion } from "framer-motion";
import { type FC, useEffect, useMemo, useRef, useState } from "react";

import type { StreamEvent } from "../../blackboardModels";

import { RetroEmpty } from "../../retro";

const STREAM_EVENT_CAP = 200;

interface StreamTabProps {
  events: StreamEvent[];
  connected: boolean;
  instanceId: string;
  /** True when older events were dropped due to the in-memory cap. */
  eventsOverflowed?: boolean;
}

type StreamMode = "all" | "actor" | "channels";

/**
 * Terminal-style activity stream with spring-animated entry, traffic-light
 * chrome header, and hairline grid background.
 */
export const StreamTab: FC<StreamTabProps> = ({
  connected,
  events,
  eventsOverflowed,
  instanceId,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<StreamMode>("all");
  const visibleEvents = useMemo(() => {
    switch (mode) {
      case "actor": {
        return events.filter((event) => event.sourceInstanceId === instanceId);
      }
      case "channels": {
        return events.filter((event) => Boolean(event.channelId));
      }
      case "all": {
        return events;
      }
      default: {
        throw assertUnreachable(mode);
      }
    }
  }, [events, instanceId, mode]);

  // Empty-state copy carries voice — three real reasons for silence:
  //   wire down · wire up + actor silent · wire up + events flowing but filtered out
  // The brand label stays put for the wire-up cases; the sub-line narrates which
  // silence the operator is looking at.
  const emptyCopy = useMemo(() => {
    if (mode !== "all" && events.length > 0) {
      return {
        label: "AWAITING SIGNAL",
        sub: `${events.length} event${events.length === 1 ? "" : "s"} captured — hidden by ${mode.toUpperCase()} filter`,
      };
    }
    if (!connected) {
      return {
        label: "SIGNAL LOST",
        sub: "reconnecting — held events resume on link",
      };
    }
    return {
      label: "AWAITING SIGNAL",
      sub: "channel hot — events stream when actor wakes",
    };
  }, [mode, connected, events.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleEvents.length]);

  return (
    <div className="flex h-full flex-col">
      {/* Terminal chrome header */}
      <div className="relative flex items-center border-b border-slate-300 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        {/* Traffic lights */}
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span
            className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${connected ? "bg-emerald-500" : "bg-slate-600"}`}
          />
        </div>
        {/* Path label */}
        <p className="absolute inset-x-0 text-center font-mono text-[10px] tracking-tight text-slate-500">
          /agents/stream.log
        </p>
        {/* Live indicator */}
        <div className="ml-auto flex items-center gap-1.5">
          {connected && (
            <span className="h-1.5 w-1.5 animate-ping rounded-full bg-emerald-500" />
          )}
          <span
            className={`font-mono text-[9px] font-bold uppercase tracking-[0.25em] ${
              connected ? "text-emerald-300" : "text-slate-500"
            }`}
          >
            [ {connected ? "LIVE" : "OFFLINE"} ]
          </span>
        </div>
      </div>

      <div className="flex border-b border-slate-200 bg-slate-50/80 px-2 py-1 dark:border-slate-800 dark:bg-slate-950/80">
        {(["all", "actor", "channels"] as const).map((entry) => {
          const active = mode === entry;
          return (
            <button
              key={entry}
              className={`border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] transition-colors ${
                active
                  ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:hover:border-slate-700 dark:hover:text-slate-300"
              }`}
              onClick={() => setMode(entry)}
              type="button"
            >
              {entry}
            </button>
          );
        })}
      </div>

      {/* Log body — hairline grid texture */}
      <div
        className="relative flex-1 overflow-y-auto bg-white dark:bg-slate-950"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff08 1px, transparent 1px), linear-gradient(to bottom, #ffffff08 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      >
        {visibleEvents.length === 0 ? (
          <RetroEmpty label={emptyCopy.label} sub={emptyCopy.sub} />
        ) : (
          <div className="p-3 pb-6">
            {eventsOverflowed && (
              <div className="mb-2 flex justify-center">
                <span className="border border-amber-400/30 bg-amber-400/5 px-3 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-amber-300/80">
                  [ BUFFER TRUNCATED · LAST {STREAM_EVENT_CAP} ]
                </span>
              </div>
            )}
            <AnimatePresence initial={false}>
              {visibleEvents.map((event) => (
                <StreamEventRow key={event.id} event={event} />
              ))}
            </AnimatePresence>
            <div ref={bottomRef} />
          </div>
        )}

        {/* Fade-out gradient at bottom */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent dark:from-slate-950" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Event row
// ---------------------------------------------------------------------------

const EVENT_CONFIG: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  activity_signal: {
    bg: "hover:bg-cyan-500/5",
    color: "text-cyan-400",
    label: "ACT",
  },
  budget_warning: {
    bg: "hover:bg-amber-500/5",
    color: "text-amber-400",
    label: "BUDGET",
  },
  cache_stats: {
    bg: "hover:bg-emerald-500/5",
    color: "text-emerald-400",
    label: "CACHE",
  },
  channel_message: {
    bg: "hover:bg-sky-500/5",
    color: "text-sky-400",
    label: "MSG",
  },
  phase_transition: {
    bg: "hover:bg-violet-500/5",
    color: "text-violet-400",
    label: "PHASE",
  },
  status_change: {
    bg: "hover:bg-emerald-500/5",
    color: "text-emerald-400",
    label: "STATUS",
  },
  token_delta: {
    bg: "hover:bg-amber-500/5",
    color: "text-amber-400",
    label: "TOKENS",
  },
  tool_call: {
    bg: "hover:bg-orange-500/5",
    color: "text-orange-400",
    label: "TOOL",
  },
};

const StreamEventRow: FC<{ event: StreamEvent }> = ({ event }) => {
  const config = EVENT_CONFIG[event.type] ?? {
    bg: "",
    color: "text-slate-400",
    label: event.type.toUpperCase(),
  };
  const time = new Date(event.timestamp).toLocaleTimeString(undefined, {
    hour12: false,
  });

  return (
    <motion.div
      className={`flex gap-3 py-0.5 pl-1 font-mono text-[11px] leading-relaxed transition-colors ${config.bg}`}
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, transition: { duration: 0.2 }, x: 12 }}
      transition={{ damping: 24, stiffness: 320, type: "spring" }}
    >
      <span className="w-[62px] flex-shrink-0 text-right text-slate-600">
        {time}
      </span>
      <span className={`w-16 flex-shrink-0 font-bold ${config.color}`}>
        [{config.label}]
      </span>
      <span className="text-slate-700 dark:text-slate-300">
        {formatEventData(event)}
      </span>
    </motion.div>
  );
};

function formatEventData(event: StreamEvent): string {
  switch (event.type) {
    case "phase_transition": {
      return `${event.from} → ${event.to}`;
    }
    case "tool_call": {
      return event.tool;
    }
    case "channel_message": {
      return event.content.length > 80
        ? `${event.content.slice(0, 80)}...`
        : event.content;
    }
    case "status_change": {
      return event.status;
    }
    case "budget_warning": {
      return event.message;
    }
    case "cache_stats": {
      const hit =
        event.cacheHitPct !== null
          ? `${event.cacheHitPct}% hit`
          : `read:${event.cacheReadTokens}`;
      const breakNote = event.isBreak ? " [break]" : "";
      return `${hit}${breakNote}`.trim();
    }
    case "token_delta": {
      const costStr = `$${event.costDeltaUsd.toFixed(4)}`;
      return `in:${event.inputTokensDelta} out:${event.outputTokensDelta} ${costStr}`;
    }
    case "activity_signal": {
      return `[${event.label}] ${event.message}`;
    }
    default: {
      throw assertUnreachable(event);
    }
  }
}
